import React from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Icon from '../AppIcon';
import { selectCurrentUser } from '../../features/auth/authSlice';
import { useGetUserDataQuery } from '../../features/api/apiSlice';
import { isProfessionalRole } from '../../lib/authPortals';

/**
 * Persistent prompt for agents who have not set office/service location.
 */
const AgentLocationBanner = ({ profilePath = '/agent-dashboard/profile' }) => {
  const authUser = useSelector(selectCurrentUser);
  const { data: userData } = useGetUserDataQuery(undefined, {
    skip: !authUser || !isProfessionalRole(authUser?.account_role),
  });

  if (!isProfessionalRole(authUser?.account_role)) return null;

  const profile = userData?.data || userData || authUser || {};
  const lat = parseFloat(profile.latitude);
  const lng = parseFloat(profile.longitude);
  const hasCoords =
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    !(lat === 0 && lng === 0) &&
    String(profile.latitude || '').trim() !== '' &&
    String(profile.longitude || '').trim() !== '';

  const city = (profile.city || '').trim();
  const hasLocation = hasCoords || city.length > 0;

  if (hasLocation && hasCoords) {
    return (
      <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-start gap-2 min-w-0">
          <Icon name="MapPin" size={18} className="text-emerald-600 mt-0.5 shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-emerald-900">Service location set</p>
            <p className="text-xs text-emerald-800/80 truncate">
              {[profile.city, profile.state, profile.country].filter(Boolean).join(', ') ||
                profile.address ||
                `${lat.toFixed(4)}, ${lng.toFixed(4)}`}
            </p>
          </div>
        </div>
        <Link
          to={profilePath}
          className="text-xs font-semibold text-emerald-800 hover:text-emerald-950 underline-offset-2 hover:underline"
        >
          Update location
        </Link>
      </div>
    );
  }

  return (
    <div className="mb-4 rounded-xl border-2 border-amber-300 bg-amber-50 px-4 py-3.5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 shadow-sm">
      <div className="flex items-start gap-3 min-w-0">
        <div className="w-10 h-10 rounded-full bg-amber-100 border border-amber-200 flex items-center justify-center shrink-0">
          <Icon name="MapPinOff" size={20} className="text-amber-700" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-bold text-amber-950">
            Set your agent location — required to appear on the map
          </p>
          <p className="text-xs text-amber-900/85 mt-0.5 leading-relaxed">
            Clients find you on the agents map using your city and coordinates. Search and select
            your office or main service area in your profile.
          </p>
        </div>
      </div>
      <Link
        to={profilePath}
        className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-slate-950 text-sm font-bold whitespace-nowrap shrink-0"
      >
        <Icon name="MapPinned" size={16} />
        Set location now
      </Link>
    </div>
  );
};

export default AgentLocationBanner;
